import {
  QualificationTier,
} from '@nxcr-org/web-api-interface/lib/domain_pb'
import { Customer } from '@nxcr-org/web-api-interface/lib/customer_service_pb'
import { Modifier } from '@nxcr-org/web-api-interface/lib/web_pb'
import { NexusService } from 'global-apis/nexus-service'
import startCase from 'lodash/startCase'
import { useContext } from 'react'
import { useIsFetching, useQuery } from '@tanstack/react-query'
import { getKeyByValue } from 'scheduling/tasks/utils'
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
  BooleanParam,
} from 'use-query-params'
import { ClientContext } from '../../../../utils/clientContext'
import {GridPaginationModel} from '@mui/x-data-grid'

const mapRows = (row: Customer.AsObject) => {
  return {
    ...row,
    cqeStatus: getCQEStatus(row.cqeStatus),
    createdAt: new Date(row.createdAt * 1000).toLocaleDateString(),
  }
}

const getCQEStatus = (status: Customer.AsObject['cqeStatus']) => {
  const cqeStatusString = startCase(
    getKeyByValue(QualificationTier, status).toLowerCase()
  )

  switch (status) {
    case 2:
    case 3:
    case 4:
      return 'Auto Approved'
    default:
      return cqeStatusString
  }
}

export function useCustomersTable() {
  const { clientId } = useContext(ClientContext)
  const [
    { page, rowsPerPage, search, cqeStatus, state, blankNames },
    setQueryParams,
  ] = useQueryParams({
    search: StringParam,
    page: withDefault(NumberParam, 0),
    rowsPerPage: withDefault(NumberParam, 25),
    cqeStatus: withDefault(NumberParam, undefined),
    state: withDefault(NumberParam, undefined),
    blankNames: withDefault(BooleanParam, false),
  })

  const setPaginationModel = (newPaginationModel: GridPaginationModel) => {
    setQueryParams({
      page: newPaginationModel.page,
      rowsPerPage: newPaginationModel.pageSize,
    })
  }

  const setQuery = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setQueryParams({ search: evt.target.value })
  }

  const setCQEStatus = (newCQEStatus: number) => {
    setQueryParams({ cqeStatus: newCQEStatus })
  }
  const setState = (newState: number) => {
    setQueryParams({ state: newState })
  }
  const setBlankNames = (newValue: boolean) => {
    setQueryParams({ blankNames: newValue })
  }
  const isFetching = useIsFetching()

  const { data: customers } = useQuery({
    queryKey: [
      '/customers',
      { page, rowsPerPage, search, cqeStatus, state, blankNames, clientId },
    ],
    queryFn: async () => {
      const query = []

      if (search)
        query.push({
          property: 'search',
          value: search,
          modifier: Modifier.EQUAL_TO,
        })
      if (cqeStatus) {
        query.push({
          property: 'cqeStatus',
          value: `${cqeStatus}`,
          modifier: Modifier.EQUAL_TO,
        })
      }
      if (clientId) {
        query.push({
          property: 'clientId',
          value: clientId,
          modifier: Modifier.EQUAL_TO,
        })
      }
      if (blankNames) {
        query.push({
          property: 'blankNames',
          // modifier: Modifier.CONTAINS,
          modifier: 13,
          value: blankNames.toString(),
        })
      }

      return NexusService.ListCustomers({
        searchParam: {
          pagination: {
            limit: rowsPerPage,
            offset: page * rowsPerPage,
          },
          query,
          order: [
            {
              property: 'updated_at',
              direction: 1,
            },
          ],
        },
      }).then((res) => {
        return {
          total: res.total,
          customers: res.dataList.map(mapRows),
        }
      })
    },
    placeholderData: (previousData) => previousData,
    enabled: !!clientId,
  }
  )

  return {
    total: customers?.total || 0,
    page,
    rowsPerPage,
    setPaginationModel,
    customers: customers?.customers || [],
    setQuery,
    setCQEStatus,
    search,
    cqeStatus,
    setBlankNames,
    state,
    setState,
    isLoading: isFetching > 0,
  }
}
